import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useAuthRemount } from '../App';
import { identifyUser, resetUser } from '../lib/posthog';
import { BACKEND_URL } from '../config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  
  // Explicit auth status for fail-closed, no-flicker UX
  // "checking" = initial state, show loading screen (NOT login page)
  // "authenticated" = user confirmed, show app
  // "unauthenticated" = no session, redirect to login
  const [authStatus, setAuthStatus] = useState('checking');
  
  // EPOCH-BASED TRACKING: Prevents authStatus from bouncing
  // authEpoch increments only on: mount, login, logout, forceAuthRemount
  // Only the matching epoch can update authStatus
  const authEpochRef = useRef(0);
  
  // DEDUPLICATION: Prevent multiple concurrent checkSession calls (StrictMode safe)
  const checkInFlightRef = useRef(false);
  
  // Get the auth remount function from App context
  const forceAuthRemount = useAuthRemount();

  useEffect(() => {
    // Initial session check on mount
    authEpochRef.current++;
    checkSession(authEpochRef.current);
  }, []);

  const checkSession = async (epoch) => {
    // DEDUPLICATION: If a check is already in progress, skip
    if (checkInFlightRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AUTH DEBUG] checkSession skipped - already in flight');
      }
      return;
    }
    
    checkInFlightRef.current = true;
    
    // EPOCH CHECK: Only set 'checking' if this is the current epoch
    // This prevents bouncing: authenticated -> checking -> authenticated
    if (epoch !== authEpochRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AUTH DEBUG] checkSession skipped - stale epoch', { epoch, currentEpoch: authEpochRef.current });
      }
      checkInFlightRef.current = false;
      return;
    }
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AUTH DEBUG] checkSession starting', {
          epoch,
          currentAuthStatus: authStatus,
          timestamp: new Date().toISOString()
        });
      }
      
      // Only set 'checking' on initial mount or explicit auth action
      // Don't bounce back to 'checking' if already authenticated
      if (authStatus !== 'authenticated') {
        setAuthStatus('checking');
        setAuthReady(false);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });

      // EPOCH CHECK: Ignore if epoch changed during fetch
      if (epoch !== authEpochRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AUTH DEBUG] Ignoring stale response', { epoch, currentEpoch: authEpochRef.current });
        }
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.log('[AUTH DEBUG] /api/auth/me SUCCESS', {
            userId: data.id,
            userRole: data.role,
            epoch,
            timestamp: new Date().toISOString()
          });
        }
        setUser(data);
        setAuthStatus('authenticated');
        setAuthReady(true);
        
        // PostHog: Identify user after successful auth
        // Note: plan_tier and org will be set separately when contexts load
        identifyUser(data, null, data.plan || 'basic');
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AUTH DEBUG] /api/auth/me FAILED', {
            status: response.status,
            epoch,
            timestamp: new Date().toISOString()
          });
        }
        setUser(null);
        setAuthStatus('unauthenticated');
        setAuthReady(true);
      }
    } catch (error) {
      // EPOCH CHECK: Ignore if epoch changed during error
      if (epoch !== authEpochRef.current) {
        return;
      }
      console.error('[AUTH DEBUG] Session check failed:', error);
      setUser(null);
      setAuthStatus('unauthenticated');
      setAuthReady(true);
    } finally {
      // Only clear in-flight flag if this is the current epoch
      if (epoch === authEpochRef.current) {
        checkInFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  const refreshUser = async () => {
    // Force refresh user data - increment epoch to allow new check
    const newEpoch = ++authEpochRef.current;
    checkInFlightRef.current = false; // Reset in-flight flag
    await checkSession(newEpoch);
  };

  const logout = async () => {
    // ═══════════════════════════════════════════════════════════════════════════════
    // COMPLETION GUARD: Don't allow logout during assessment completion flow
    // This prevents the reload bug where modal appears briefly then page reloads
    // ═══════════════════════════════════════════════════════════════════════════════
    if (window.__MM_COMPLETION_IN_PROGRESS) {
      console.log('[AUTH DEBUG] Logout BLOCKED - completion in progress');
      return;
    }
    
    // Increment epoch to invalidate any in-flight requests
    authEpochRef.current++;
    
    // PostHog: Reset user on logout
    resetUser();
    
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all stale auth and nav cache
      setUser(null);
      setAuthReady(false);
      
      // Clear any cached auth state from localStorage/sessionStorage
      const keysToRemove = [
        'user', 'role', 'isAdmin', 'activeOrg', 'navMode',
        'impersonated_user_name', 'impersonated_user_email'
      ];
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      
      // CRITICAL: Force AuthProvider remount to destroy stale state
      if (process.env.NODE_ENV === 'development') {
        console.log('[AUTH DEBUG] Logout - calling forceAuthRemount');
      }
      forceAuthRemount();
      
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider value={{ user, logout, loading, refreshUser, authReady, authStatus }}>
      {children}
    </AuthContext.Provider>
  );
};
